import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import SEO from '../components/SEO';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await api.get('/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.valid) {
        navigate('/admin');
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminName');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, admin_email, admin_name } = response.data;
      
      localStorage.setItem('adminToken', access_token);
      localStorage.setItem('adminEmail', admin_email);
      localStorage.setItem('adminName', admin_name);
      
      toast.success('Giriş başarılı!');
      navigate('/admin');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Geçersiz email veya şifre');
      } else {
        toast.error('Giriş yapılırken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/setup', { email, password, name });
      toast.success('Admin hesabı oluşturuldu! Şimdi giriş yapabilirsiniz.');
      setIsSetup(false);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Hesap oluşturulurken hata');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <SEO title="Yönetim" noindex />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold">
              <span className="text-gray-900">istanbul</span>
              <span className="text-orange-500">turda</span>
            </h1>
            <p className="text-gray-500 mt-2">Yönetim Paneli</p>
          </div>

          {/* Form */}
          <form onSubmit={isSetup ? handleSetup : handleLogin} className="space-y-5">
            {isSetup && (
              <div>
                <Label htmlFor="name">Ad Soyad</Label>
                <div className="relative mt-1">
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Admin Adı"
                    className="pl-10"
                    required
                    data-testid="admin-name-input"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">👤</span>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email">E-posta</Label>
              <div className="relative mt-1">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@istanbulturda.com"
                  className="pl-10"
                  required
                  data-testid="admin-email-input"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Şifre</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  required
                  minLength={6}
                  data-testid="admin-password-input"
                />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-bold rounded-xl"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Bekleyin...' : (isSetup ? 'Hesap Oluştur' : 'Giriş Yap')}
            </Button>
          </form>

          {/* Setup Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSetup(!isSetup)}
              className="text-sm text-gray-500 hover:text-orange-500"
            >
              {isSetup ? 'Zaten hesabım var, giriş yap' : 'İlk kez mi kurulum yapıyorsunuz?'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          VIATERRA TRAVEL AGENCY © 2026
        </p>
      </div>
    </div>
  );
}
